/**
 * Contact Us — sales enquiry page for the marketing site. Same design system
 * as the homepage (dark/light theme, NavBar/Footer from ../home/marketing-shell).
 * Submits to POST /public/enquiries, which creates a super-admin-only record
 * in the master DB (see backend/src/models/PlatformEnquiry.model.ts) — no
 * tenant is ever involved.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ArrowRight, Check, Clock, Shield, Users } from 'lucide-react';
import { ThemeCtx, useTheme, useThemeState, THEMES, NavBar, Footer } from '../home/marketing-shell';
import { submitEnquiry } from '@/services/public.api';

const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-1000', '1000+'];
const MESSAGE_VOLUME_OPTIONS = ['<1k', '1k-10k', '10k-100k', '100k+'];
const PLAN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'undecided', label: "Not sure yet" },
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
];
const HOW_HEARD_OPTIONS = ['Search engine', 'Social media', 'Referral', 'Advertisement', 'Blog / content', 'Other'];

const TRUST_POINTS = [
  { icon: Clock, text: 'We reply within one business day' },
  { icon: Shield, text: 'Official Meta Business Partner & WhatsApp BSP' },
  { icon: Users, text: 'Trusted by 210,000+ businesses across 68+ countries' },
];

interface FormState {
  fullName: string;
  workEmail: string;
  phone: string;
  companyName: string;
  companySize: string;
  role: string;
  country: string;
  messageVolume: string;
  message: string;
  planInterest: string;
  howHeard: string;
}

function ContactForm() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';
  const [searchParams] = useSearchParams();
  const planFromQuery = searchParams.get('plan');
  const initialPlan = PLAN_OPTIONS.some((p) => p.value === planFromQuery) ? planFromQuery! : 'undecided';

  const [form, setForm] = useState<FormState>({
    fullName: '',
    workEmail: '',
    phone: '',
    companyName: '',
    companySize: '',
    role: '',
    country: '',
    messageVolume: '',
    message: '',
    planInterest: initialPlan,
    howHeard: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm((f) => ({ ...f, [key]: e.target.value }));

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.fullName.trim()) next.fullName = 'Required';
    if (!form.workEmail.trim()) next.workEmail = 'Required';
    else if (!/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(form.workEmail.trim())) next.workEmail = 'Enter a valid email';
    if (!form.companyName.trim()) next.companyName = 'Required';
    if (!form.phone.trim()) next.phone = 'Required';
    else {
      const digits = form.phone.replace(/\D/g, '');
      const parsed = parsePhoneNumberFromString(`+${digits}`);
      if (!parsed?.isValid()) next.phone = 'Enter a valid phone number with country code';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      await submitEnquiry({
        fullName: form.fullName.trim(),
        workEmail: form.workEmail.trim(),
        phone: form.phone.replace(/\D/g, ''),
        companyName: form.companyName.trim(),
        planInterest: form.planInterest,
        ...(form.companySize ? { companySize: form.companySize } : {}),
        ...(form.role.trim() ? { role: form.role.trim() } : {}),
        ...(form.country.trim() ? { country: form.country.trim() } : {}),
        ...(form.messageVolume ? { messageVolume: form.messageVolume } : {}),
        ...(form.message.trim() ? { message: form.message.trim() } : {}),
        ...(form.howHeard ? { howHeard: form.howHeard } : {}),
      });
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error || err?.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    borderRadius: 10,
    background: t.muted,
    border: `1px solid ${t.border}`,
    color: t.fg,
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12.5, fontWeight: 600, color: t.mutedFg, marginBottom: 6,
  };
  const errorStyle: React.CSSProperties = { fontSize: 11.5, color: '#ef4444', marginTop: 4 };

  const focusHandlers = {
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      e.currentTarget.style.borderColor = t.green;
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      e.currentTarget.style.borderColor = t.border;
    },
  };

  if (submitted) {
    return (
      <div style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 24, padding: '48px 32px',
        textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', background: t.greenDim,
          border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Check size={26} color={t.green} strokeWidth={3} />
        </div>
        <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 22, color: t.fg }}>
          Thanks — message received
        </h3>
        <p style={{ color: t.mutedFg, fontSize: 15, maxWidth: 380, lineHeight: 1.6 }}>
          We'll be in touch within one business day. In the meantime, feel free to explore the rest
          of the site.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 24, padding: '32px 28px',
        display: 'flex', flexDirection: 'column', gap: 18,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="contact-grid-2">
        <div>
          <label style={labelStyle}>Full name *</label>
          <input className="baatzy-field" style={inputStyle} value={form.fullName} onChange={set('fullName')} {...focusHandlers} />
          {errors.fullName && <div style={errorStyle}>{errors.fullName}</div>}
        </div>
        <div>
          <label style={labelStyle}>Work email *</label>
          <input className="baatzy-field" style={inputStyle} value={form.workEmail} onChange={set('workEmail')} placeholder="you@company.com" {...focusHandlers} />
          {errors.workEmail && <div style={errorStyle}>{errors.workEmail}</div>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="contact-grid-2">
        <div>
          <label style={labelStyle}>Phone (with country code) *</label>
          <input className="baatzy-field" style={inputStyle} value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" {...focusHandlers} />
          {errors.phone && <div style={errorStyle}>{errors.phone}</div>}
        </div>
        <div>
          <label style={labelStyle}>Company name *</label>
          <input className="baatzy-field" style={inputStyle} value={form.companyName} onChange={set('companyName')} {...focusHandlers} />
          {errors.companyName && <div style={errorStyle}>{errors.companyName}</div>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="contact-grid-2">
        <div>
          <label style={labelStyle}>Company size</label>
          <select className="baatzy-field" style={inputStyle} value={form.companySize} onChange={set('companySize')} {...focusHandlers}>
            <option value="">Select...</option>
            {COMPANY_SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o} employees</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Your role</label>
          <input className="baatzy-field" style={inputStyle} value={form.role} onChange={set('role')} placeholder="e.g. Head of Marketing" {...focusHandlers} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="contact-grid-2">
        <div>
          <label style={labelStyle}>Country</label>
          <input className="baatzy-field" style={inputStyle} value={form.country} onChange={set('country')} {...focusHandlers} />
        </div>
        <div>
          <label style={labelStyle}>Monthly WhatsApp message volume</label>
          <select className="baatzy-field" style={inputStyle} value={form.messageVolume} onChange={set('messageVolume')} {...focusHandlers}>
            <option value="">Select...</option>
            {MESSAGE_VOLUME_OPTIONS.map((o) => <option key={o} value={o}>{o} messages/mo</option>)}
          </select>
        </div>
      </div>

      <div>
        <label style={labelStyle}>Which plan are you interested in?</label>
        <select className="baatzy-field" style={inputStyle} value={form.planInterest} onChange={set('planInterest')} {...focusHandlers}>
          {PLAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div>
        <label style={labelStyle}>What are you looking to solve?</label>
        <textarea
          className="baatzy-field"
          style={{ ...inputStyle, minHeight: 96, resize: 'vertical', fontFamily: "'Inter', sans-serif" }}
          value={form.message}
          onChange={set('message')}
          placeholder="Tell us a bit about your use case..."
          {...focusHandlers}
        />
      </div>

      <div>
        <label style={labelStyle}>How did you hear about us? (optional)</label>
        <select className="baatzy-field" style={inputStyle} value={form.howHeard} onChange={set('howHeard')} {...focusHandlers}>
          <option value="">Select...</option>
          {HOW_HEARD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      {submitError && (
        <div style={{ color: '#ef4444', fontSize: 13, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px' }}>
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '14px 24px', borderRadius: 12, border: 'none', cursor: submitting ? 'default' : 'pointer',
          background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
          fontFamily: 'Manrope, sans-serif', opacity: submitting ? 0.7 : 1,
          boxShadow: `0 8px 30px ${isDark ? 'rgba(61,220,132,0.35)' : 'rgba(37,211,102,0.4)'}`,
        }}
      >
        {submitting ? 'Sending...' : 'Send message'} {!submitting && <ArrowRight size={16} />}
      </button>

      <style>{`
        .contact-grid-2 { @media (max-width: 640px) { grid-template-columns: 1fr !important; } }
        .baatzy-field:-webkit-autofill,
        .baatzy-field:-webkit-autofill:hover,
        .baatzy-field:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px ${t.muted} inset;
          -webkit-text-fill-color: ${t.fg};
          caret-color: ${t.fg};
          transition: background-color 9999s ease-in-out 0s;
        }
      `}</style>
    </form>
  );
}

function ContactHero() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';

  return (
    <section style={{ position: 'relative', paddingTop: 130, paddingBottom: 80, overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: isDark
          ? 'radial-gradient(ellipse 120% 60% at 50% 0%, rgba(61,220,132,0.08) 0%, transparent 60%), #0D1117'
          : 'radial-gradient(ellipse 120% 60% at 50% 0%, rgba(37,211,102,0.10) 0%, transparent 60%), #F0FFF6',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', maxWidth: 1100, margin: '0 auto', padding: '0 24px', display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 56, alignItems: 'start' }} className="contact-hero-grid">
        {/* left: pitch */}
        <div>
          <span style={{ display: 'inline-block', padding: '6px 16px', borderRadius: 50, background: t.greenDim, border: `1px solid ${t.border}`, fontSize: 11, color: t.green, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 24 }}>
            Contact Sales
          </span>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 'clamp(2.2rem, 4.5vw, 3.4rem)', lineHeight: 1.1, letterSpacing: '-1px', color: t.fg, marginBottom: 20 }}>
            Let's talk about your business
          </h1>
          <p style={{ color: t.mutedFg, fontSize: 16.5, lineHeight: 1.75, marginBottom: 36, maxWidth: 420 }}>
            Tell us a bit about what you're building and we'll help you find the right plan —
            no generic sales pitch, just a straight answer on what fits.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {TRUST_POINTS.map(({ icon: Icon, text }) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 10, background: t.greenDim, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={15} color={t.green} />
                </span>
                <span style={{ color: t.fg, fontSize: 14 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* right: form */}
        <ContactForm />
      </div>

      <style>{`.contact-hero-grid { @media (max-width: 900px) { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

export default function Contact() {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];

  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div style={{ minHeight: '100vh', background: t.bg, color: t.fg, fontFamily: "'Inter', sans-serif" }}>
        <NavBar />
        <main>
          <ContactHero />
        </main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
